import { z } from 'zod';

/**
 * The report inbox (OPS-63, fleet-agent ADR 0004 row B4).
 *
 * A person reports something in free text — today on the fleet page. An
 * intake run claims the report, may ask its reporter one question, and ends it
 * with an outcome (usually a filed ticket) or as unplaceable. The report's
 * state IS the claim ledger: there is one row per report, and every
 * transition is a locked compare-and-set on it, so two runs can never both
 * hold one.
 *
 *   open ──claim──▶ claimed ──park──▶ parked ──reply──▶ open
 *                     │                 │
 *                     │                 └─deadline / release─▶ released ──reply──▶ open
 *                     ├─release──▶ released
 *                     ├─finish───▶ finished   (terminal; a reply is kept, nothing reopens)
 *                     └─unplaceable─▶ unplaceable ──reply──▶ open
 *
 * `open` reports are what the intake dispatches from. A reply to a parked,
 * released or unplaceable report reopens it with the reply in its
 * conversation, which is how the reporter's answer reaches the next run.
 */
export const FLEET_REPORT_TEXT_MAX_CHARS = 8_000;
export const FLEET_REPORT_MESSAGE_MAX_CHARS = 4_000;
export const FLEET_REPORT_QUESTION_MAX_CHARS = 2_000;
export const FLEET_REPORT_OUTCOME_MAX_CHARS = 500;
export const FLEET_REPORT_WORKER_MAX_CHARS = 120;
/** How long a parked report waits for its reporter before the backend releases it (Buzz's "I will give up in 24 hours"). */
export const FLEET_REPORT_QUESTION_DEADLINE_MS = 24 * 60 * 60 * 1000;
/**
 * How long a claim may sit without a park, finish or unplaceable before the
 * backend releases it: a run that died holding a report would otherwise hold
 * it forever, and the claim's only release path would run inside the process
 * that took it (BL-076).
 */
export const FLEET_REPORT_CLAIM_EXPIRES_AFTER_MS = 4 * 60 * 60 * 1000;
export const FLEET_REPORT_LIST_LIMIT = 100;

export const FLEET_REPORT_STATES = ['open', 'claimed', 'parked', 'released', 'finished', 'unplaceable'] as const;
export const FleetReportStateSchema = z.enum(FLEET_REPORT_STATES);
export type FleetReportState = z.infer<typeof FleetReportStateSchema>;

/** Who wrote a conversation message: the person who reported, or the run working on it. */
export const FLEET_REPORT_MESSAGE_AUTHORS = ['reporter', 'agent'] as const;
export const FleetReportMessageAuthorSchema = z.enum(FLEET_REPORT_MESSAGE_AUTHORS);
export type FleetReportMessageAuthor = z.infer<typeof FleetReportMessageAuthorSchema>;

/** Where a reporter's message came from. Lowercase wire values, like `MESSENGERS`. `null` on an agent's message. */
export const FLEET_REPORT_CHANNELS = ['page', 'whatsapp', 'telegram'] as const;
export const FleetReportChannelSchema = z.enum(FLEET_REPORT_CHANNELS);
export type FleetReportChannel = z.infer<typeof FleetReportChannelSchema>;

/** Why a report was released: its question went unanswered, its claim lapsed, or the run handed it back. */
export const FLEET_REPORT_RELEASE_CAUSES = ['deadline', 'claim-expired', 'worker'] as const;
export const FleetReportReleaseCauseSchema = z.enum(FLEET_REPORT_RELEASE_CAUSES);
export type FleetReportReleaseCause = z.infer<typeof FleetReportReleaseCauseSchema>;

export const FleetReportTicketSchema = z.string().regex(/^[A-Z][A-Z0-9]*-\d+$/u);

export const FleetReportViewSchema = z.object({
  id: z.uuid(),
  text: z.string(),
  reporterName: z.string(),
  submittedAt: z.iso.datetime(),
  state: FleetReportStateSchema,
  /** The run's worker while it holds or last held the report; `null` while it was never claimed. */
  worker: z.string().nullable(),
  claimedAt: z.iso.datetime().nullable(),
  /** The question of the latest park, kept after the park ends so the page can show what was asked. */
  question: z.string().nullable(),
  parkedAt: z.iso.datetime().nullable(),
  /** When the backend releases the report if nobody answers; `null` unless parked. */
  deadlineAt: z.iso.datetime().nullable(),
  releasedAt: z.iso.datetime().nullable(),
  releaseCause: FleetReportReleaseCauseSchema.nullable(),
  releaseReason: z.string().nullable(),
  /** One sentence for somebody reading it cold, e.g. `filed as IMM-812 — Deal page crashes on archive`. */
  outcome: z.string().nullable(),
  ticket: FleetReportTicketSchema.nullable(),
  ticketUrl: z.url().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});

export type FleetReportView = z.infer<typeof FleetReportViewSchema>;

export const FleetReportMessageViewSchema = z.object({
  id: z.uuid(),
  author: FleetReportMessageAuthorSchema,
  /** The reporter's name, or the run's worker. */
  authorName: z.string(),
  body: z.string(),
  channel: FleetReportChannelSchema.nullable(),
  createdAt: z.iso.datetime(),
});

export type FleetReportMessageView = z.infer<typeof FleetReportMessageViewSchema>;

/** A report with its whole conversation, oldest message first. */
export const FleetReportDetailSchema = FleetReportViewSchema.extend({
  messages: z.array(FleetReportMessageViewSchema),
});

export type FleetReportDetail = z.infer<typeof FleetReportDetailSchema>;

export const FleetReportPathParamsSchema = z.object({ reportId: z.uuid() });

export const ListFleetReportsResponseSchema = z.object({ data: z.array(FleetReportViewSchema) });

export type ListFleetReportsResponse = z.infer<typeof ListFleetReportsResponseSchema>;

export const SubmitFleetReportBodySchema = z.object({
  text: z.string().trim().min(1).max(FLEET_REPORT_TEXT_MAX_CHARS),
});

export type SubmitFleetReportBody = z.infer<typeof SubmitFleetReportBodySchema>;

export const ReplyToFleetReportBodySchema = z.object({
  body: z.string().trim().min(1).max(FLEET_REPORT_MESSAGE_MAX_CHARS),
});

export type ReplyToFleetReportBody = z.infer<typeof ReplyToFleetReportBodySchema>;

// --- the machine-facing transitions ----------------------------------------

const WorkerSchema = z.string().trim().min(1).max(FLEET_REPORT_WORKER_MAX_CHARS);

export const ClaimFleetReportBodySchema = z.object({ worker: WorkerSchema });

export type ClaimFleetReportBody = z.infer<typeof ClaimFleetReportBodySchema>;

/**
 * `held` — the caller's worker holds the report (a repeated claim is `held` too).
 * `stood-down` — another worker holds it, or it is no longer open; `report` says which.
 * Not an error: standing down is the expected end of losing the race, and the run branches on it.
 */
export const FLEET_REPORT_CLAIM_OUTCOMES = ['held', 'stood-down'] as const;
export const FleetReportClaimOutcomeSchema = z.enum(FLEET_REPORT_CLAIM_OUTCOMES);
export type FleetReportClaimOutcome = z.infer<typeof FleetReportClaimOutcomeSchema>;

export const ClaimFleetReportResponseSchema = z.object({
  outcome: FleetReportClaimOutcomeSchema,
  report: FleetReportViewSchema,
});

export type ClaimFleetReportResponse = z.infer<typeof ClaimFleetReportResponseSchema>;

export const SayOnFleetReportBodySchema = z.object({
  worker: WorkerSchema,
  body: z.string().trim().min(1).max(FLEET_REPORT_MESSAGE_MAX_CHARS),
});

export type SayOnFleetReportBody = z.infer<typeof SayOnFleetReportBodySchema>;

export const ParkFleetReportBodySchema = z.object({
  worker: WorkerSchema,
  question: z.string().trim().min(1).max(FLEET_REPORT_QUESTION_MAX_CHARS),
});

export type ParkFleetReportBody = z.infer<typeof ParkFleetReportBodySchema>;

export const ReleaseFleetReportBodySchema = z.object({
  worker: WorkerSchema,
  reason: z.string().trim().min(1).max(FLEET_REPORT_OUTCOME_MAX_CHARS),
});

export type ReleaseFleetReportBody = z.infer<typeof ReleaseFleetReportBodySchema>;

export const FinishFleetReportBodySchema = z.object({
  worker: WorkerSchema,
  outcome: z.string().trim().min(1).max(FLEET_REPORT_OUTCOME_MAX_CHARS),
  ticket: FleetReportTicketSchema.optional(),
  ticketUrl: z.url().optional(),
});

export type FinishFleetReportBody = z.infer<typeof FinishFleetReportBodySchema>;

export const MarkFleetReportUnplaceableBodySchema = z.object({ worker: WorkerSchema });

export type MarkFleetReportUnplaceableBody = z.infer<typeof MarkFleetReportUnplaceableBodySchema>;

export const FleetReportTransitionResponseSchema = z.object({ report: FleetReportViewSchema });

export type FleetReportTransitionResponse = z.infer<typeof FleetReportTransitionResponseSchema>;

export const FleetReportMessageResponseSchema = z.object({ message: FleetReportMessageViewSchema });

export type FleetReportMessageResponse = z.infer<typeof FleetReportMessageResponseSchema>;

// --- release announcements -------------------------------------------------

/**
 * Which product's release process announced a release (IMM-1418). Lowercase wire
 * values, like `FLEET_REPORT_CHANNELS`. A release is unique per product and version.
 */
export const FLEET_RELEASE_PRODUCTS = ['immoos', 'landiq'] as const;
export const FleetReleaseProductSchema = z.enum(FLEET_RELEASE_PRODUCTS);
export type FleetReleaseProduct = z.infer<typeof FleetReleaseProductSchema>;

/**
 * A production release, announced by `/feature-deploy` (OPS-63). Its own
 * record, never a report: the intake reads only `fleet_reports`, so an
 * announcement can never be mistaken for one — the job Buzz's `--release`
 * tag did.
 */
export const FLEET_RELEASE_VERSION_MAX_CHARS = 64;
export const FLEET_RELEASE_NOTES_MAX_CHARS = 20_000;
export const FLEET_RELEASE_LIST_LIMIT = 50;
/** Most ticket IDs one announcement may carry. A longer list is refused (400), never truncated, so a count is never understated. */
export const FLEET_RELEASE_TICKETS_MAX = 500;

export const FleetReleaseViewSchema = z.object({
  id: z.uuid(),
  product: FleetReleaseProductSchema,
  version: z.string(),
  notes: z.string().nullable(),
  /** The machine that announced it. */
  announcedBy: z.string(),
  announcedAt: z.iso.datetime(),
});

export type FleetReleaseView = z.infer<typeof FleetReleaseViewSchema>;

export const AnnounceFleetReleaseBodySchema = z.object({
  /** Absent: `immoos`. Every announcer before LIQ-910 is immoOS's `/feature-deploy`. */
  product: FleetReleaseProductSchema.optional(),
  version: z.string().trim().min(1).max(FLEET_RELEASE_VERSION_MAX_CHARS),
  notes: z.string().trim().min(1).max(FLEET_RELEASE_NOTES_MAX_CHARS).optional(),
  /**
   * The Linear IDs of the tickets this release moved to Deployed (IMM-1418). The first announcement that
   * stores the release pushes their count to operators; absent or empty pushes nothing.
   */
  tickets: z.array(FleetReportTicketSchema).max(FLEET_RELEASE_TICKETS_MAX).optional(),
});

export type AnnounceFleetReleaseBody = z.infer<typeof AnnounceFleetReleaseBodySchema>;

/** `announced: false` — this product announced this version before; the first announcement is returned unchanged. */
export const AnnounceFleetReleaseResponseSchema = z.object({
  announced: z.boolean(),
  release: FleetReleaseViewSchema,
});

export type AnnounceFleetReleaseResponse = z.infer<typeof AnnounceFleetReleaseResponseSchema>;

/**
 * `POST /v1/fleet/releases/blocked` — the deployer could not ship the release: a one-way notice to the tech person
 * (a messenger message), nothing recorded on the fleet page. Not idempotent per version: a retried call notices again.
 */
export const BlockFleetReleaseBodySchema = z.object({
  version: z.string().trim().min(1).max(FLEET_RELEASE_VERSION_MAX_CHARS),
  notes: z.string().trim().min(1).max(20_000),
});

export type BlockFleetReleaseBody = z.infer<typeof BlockFleetReleaseBodySchema>;

/** `noticed: false` — no messenger is wired, so nobody was told. */
export const BlockFleetReleaseResponseSchema = z.object({ noticed: z.boolean() });

export type BlockFleetReleaseResponse = z.infer<typeof BlockFleetReleaseResponseSchema>;

export const ListFleetReleasesResponseSchema = z.object({ data: z.array(FleetReleaseViewSchema) });

export type ListFleetReleasesResponse = z.infer<typeof ListFleetReleasesResponseSchema>;
